import { jsPDF } from 'jspdf'
import { TaskPdfData, TaskPdfSubtask, PRIORITY_LABELS } from './types'
import dayjs from 'dayjs'
import 'dayjs/locale/en'
import { FrequencyType } from 'components/NewsFeed/PollTaskForm/utils'
import { calculateTots } from '../utils'
import keys from 'constants/keys'
import { richTextToPlainText } from 'components/CommonComponents/RichTextEditor'

/** Returns the best available plain text from a field that may have rich_text or legacy text */
const resolveText = (text?: string | null, rich_text?: any): string => {
  if (text?.trim()) return text
  if (rich_text) return richTextToPlainText(rich_text)
  return ''
}

// ============================================================================
// CONSTANTS
// ============================================================================

const PAGE = {
  WIDTH: 210,
  HEIGHT: 297,
  MARGIN: 20,
  get CONTENT_WIDTH() {
    return this.WIDTH - this.MARGIN * 2
  },
}

// Spacing values
const SPACE = {
  XS: 4,
  SM: 8,
  MD: 12,
  LG: 16,
}

// Typography
const FONT = {
  HEADER: 20,
  BLOCK_LABEL: 10,
  BLOCK_VALUE: 10,
  SUBTASK_TYPE: 8,
  SUBTASK_TITLE: 12,
  META: 9,
  DESCRIPTION_LABEL: 8,
  DESCRIPTION_TEXT: 10,
  BADGE: 7,
  FOOTER: 7,
}

// Colors
const COLOR = {
  PRIMARY: '#111111',
  MUTED: '#6B7280',
  LABEL: '#9CA3AF',
  PINK_BG: '#FADADD',
  GRAY_BG: '#F3F4F6',
  REQUIRED_BG: '#FEE2E2',
  REQUIRED_TEXT: '#991B1B',
  CATEGORY_BULLET: '#3B82F6',
  BADGE_BG: '#111111',
  BADGE_TEXT: '#FFFFFF',
}

// Media dimensions — fixed container for all images (object-contain)
const MEDIA = {
  CONTAINER_WIDTH: 116, // Fixed width (20% smaller: 144.5 * 0.8 ≈ 116)
  CONTAINER_HEIGHT: 80, // Fixed height (20% smaller: 100 * 0.8 = 80)
  GAP: 8,
}

// Block padding
const PADDING = {
  H: 8,
  V: 8,
}

// ============================================================================
// LAYOUT ENGINE
// ============================================================================

interface LayoutContext {
  doc: jsPDF
  y: number
  singlePage?: boolean
}

const ensureSpace = (ctx: LayoutContext, height: number): void => {
  if (!ctx.singlePage && ctx.y + height > PAGE.HEIGHT - PAGE.MARGIN) {
    ctx.doc.addPage()
    ctx.y = PAGE.MARGIN
  }
}

// ============================================================================
// TOP HEADER
// ============================================================================

const drawTopHeader = (ctx: LayoutContext, title: string): void => {
  const height = PAGE.HEIGHT * 0.12

  ctx.doc.setFillColor(COLOR.PINK_BG)
  ctx.doc.rect(0, 0, PAGE.WIDTH, height, 'F')

  ctx.doc.setFontSize(FONT.HEADER)
  ctx.doc.setFont('helvetica', 'bold')
  ctx.doc.setTextColor(COLOR.PRIMARY)
  ctx.doc.text(title.toUpperCase(), PAGE.MARGIN, height / 2 + 3)

  ctx.y = height + SPACE.SM
}

// ============================================================================
// TASK SUMMARY BLOCKS
// ============================================================================

const drawTaskSummaryBlock = (ctx: LayoutContext, label: string, value: string): void => {
  ctx.doc.setFontSize(FONT.BLOCK_VALUE)

  const lines = ctx.doc.splitTextToSize(value, PAGE.CONTENT_WIDTH - PADDING.H * 2)
  const textHeight = lines.length * FONT.BLOCK_VALUE * 0.4
  const blockHeight = PADDING.V + 6 + textHeight + PADDING.V

  ensureSpace(ctx, blockHeight + SPACE.XS)

  ctx.doc.setFillColor(COLOR.GRAY_BG)
  ctx.doc.roundedRect(PAGE.MARGIN, ctx.y, PAGE.CONTENT_WIDTH, blockHeight, 2, 2, 'F')

  ctx.doc.setFontSize(FONT.BLOCK_LABEL)
  ctx.doc.setFont('helvetica', 'bold')
  ctx.doc.setTextColor(COLOR.LABEL)
  ctx.doc.text(label.toUpperCase(), PAGE.MARGIN + PADDING.H, ctx.y + PADDING.V + 4)

  ctx.doc.setFontSize(FONT.BLOCK_VALUE)
  ctx.doc.setFont('helvetica', 'normal')
  ctx.doc.setTextColor(COLOR.PRIMARY)

  let textY = ctx.y + PADDING.V + 10

  for (const line of lines) {
    ctx.doc.text(line, PAGE.MARGIN + PADDING.H, textY)
    textY += FONT.BLOCK_VALUE * 0.4
  }

  ctx.y += blockHeight + SPACE.XS
}

const drawTwoColumnBlocks = (
  ctx: LayoutContext,
  left: { label: string; value: string },
  right: { label: string; value: string }
): void => {
  const colWidth = (PAGE.CONTENT_WIDTH - SPACE.SM) / 2
  const blockHeight = PADDING.V + 6 + FONT.BLOCK_VALUE * 0.4 + PADDING.V

  ensureSpace(ctx, blockHeight + SPACE.XS)

  // Left
  ctx.doc.setFillColor(COLOR.GRAY_BG)
  ctx.doc.roundedRect(PAGE.MARGIN, ctx.y, colWidth, blockHeight, 2, 2, 'F')

  ctx.doc.setFontSize(FONT.BLOCK_LABEL)
  ctx.doc.setFont('helvetica', 'bold')
  ctx.doc.setTextColor(COLOR.LABEL)
  ctx.doc.text(left.label.toUpperCase(), PAGE.MARGIN + PADDING.H, ctx.y + PADDING.V + 4)

  ctx.doc.setFontSize(FONT.BLOCK_VALUE)
  ctx.doc.setFont('helvetica', 'normal')
  ctx.doc.setTextColor(COLOR.PRIMARY)
  ctx.doc.text(left.value, PAGE.MARGIN + PADDING.H, ctx.y + PADDING.V + 10)

  // Right
  const rightX = PAGE.MARGIN + colWidth + SPACE.SM

  ctx.doc.setFillColor(COLOR.GRAY_BG)
  ctx.doc.roundedRect(rightX, ctx.y, colWidth, blockHeight, 2, 2, 'F')

  ctx.doc.setFontSize(FONT.BLOCK_LABEL)
  ctx.doc.setFont('helvetica', 'bold')
  ctx.doc.setTextColor(COLOR.LABEL)
  ctx.doc.text(right.label.toUpperCase(), rightX + PADDING.H, ctx.y + PADDING.V + 4)

  ctx.doc.setFontSize(FONT.BLOCK_VALUE)
  ctx.doc.setFont('helvetica', 'normal')
  ctx.doc.setTextColor(COLOR.PRIMARY)
  ctx.doc.text(right.value, rightX + PADDING.H, ctx.y + PADDING.V + 10)

  ctx.y += blockHeight + SPACE.XS
}

const drawOptionsBlock = (ctx: LayoutContext, options: string[]): void => {
  if (options.length === 0) return

  const lineHeight = FONT.BLOCK_VALUE * 0.4
  const textHeight = options.length * (lineHeight + 2)
  const blockHeight = PADDING.V + 6 + textHeight + PADDING.V

  ensureSpace(ctx, blockHeight + SPACE.XS)

  ctx.doc.setFillColor(COLOR.GRAY_BG)
  ctx.doc.roundedRect(PAGE.MARGIN, ctx.y, PAGE.CONTENT_WIDTH, blockHeight, 2, 2, 'F')

  ctx.doc.setFontSize(FONT.BLOCK_LABEL)
  ctx.doc.setFont('helvetica', 'bold')
  ctx.doc.setTextColor(COLOR.LABEL)
  ctx.doc.text('OPTIONS', PAGE.MARGIN + PADDING.H, ctx.y + PADDING.V + 4)

  ctx.doc.setFontSize(FONT.BLOCK_VALUE)
  ctx.doc.setFont('helvetica', 'normal')
  ctx.doc.setTextColor(COLOR.PRIMARY)

  let textY = ctx.y + PADDING.V + 10

  for (const option of options) {
    ctx.doc.setFillColor(COLOR.MUTED)
    ctx.doc.circle(PAGE.MARGIN + PADDING.H + 1, textY - 1, 0.8, 'F')
    ctx.doc.text(option, PAGE.MARGIN + PADDING.H + 4, textY)
    textY += lineHeight + 2
  }

  ctx.y += blockHeight + SPACE.XS
}

// ============================================================================
// SUBTASK HEADER
// ============================================================================

const drawSubtaskHeader = (ctx: LayoutContext, title: string, typeLabel: string): void => {
  const line1Height = FONT.SUBTASK_TYPE * 0.4
  const line2Height = FONT.SUBTASK_TITLE * 0.4
  const stripeHeight = PADDING.V + line1Height + 2 + line2Height + PADDING.V

  ensureSpace(ctx, stripeHeight + 30)

  ctx.doc.setFillColor(COLOR.GRAY_BG)
  ctx.doc.rect(0, ctx.y, PAGE.WIDTH, stripeHeight, 'F')

  ctx.doc.setFontSize(FONT.SUBTASK_TYPE)
  ctx.doc.setFont('helvetica', 'bold')
  ctx.doc.setTextColor(COLOR.MUTED)
  ctx.doc.text(typeLabel.toUpperCase(), PAGE.MARGIN, ctx.y + PADDING.V + line1Height)

  ctx.doc.setFontSize(FONT.SUBTASK_TITLE)
  ctx.doc.setFont('helvetica', 'bold')
  ctx.doc.setTextColor(COLOR.PRIMARY)
  ctx.doc.text(title, PAGE.MARGIN, ctx.y + PADDING.V + line1Height + 2 + line2Height)

  ctx.y += stripeHeight + SPACE.MD // More space after stripe
}

// ============================================================================
// META ROW — With proper spacing
// ============================================================================

const drawMetaRow = (ctx: LayoutContext, category?: string, isRequired?: boolean): void => {
  if (!category && !isRequired) return

  let x = PAGE.MARGIN

  // Category with bullet
  if (category) {
    ctx.doc.setFillColor(COLOR.CATEGORY_BULLET)
    ctx.doc.circle(x + 1.5, ctx.y - 1, 1.2, 'F')

    ctx.doc.setFontSize(FONT.META)
    ctx.doc.setFont('helvetica', 'normal')
    ctx.doc.setTextColor(COLOR.MUTED)
    ctx.doc.text(category, x + 5, ctx.y)

    x += 5 + ctx.doc.getTextWidth(category) + SPACE.MD // More gap before Required
  }

  // Required badge
  if (isRequired) {
    ctx.doc.setFontSize(FONT.META)
    const badgeText = 'Required'
    const badgeWidth = ctx.doc.getTextWidth(badgeText) + 8
    const badgeHeight = 4.5

    ctx.doc.setFillColor(COLOR.REQUIRED_BG)
    ctx.doc.roundedRect(x, ctx.y - 3.5, badgeWidth, badgeHeight, 2, 2, 'F')

    ctx.doc.setFont('helvetica', 'bold')
    ctx.doc.setTextColor(COLOR.REQUIRED_TEXT)
    ctx.doc.text(badgeText, x + 4, ctx.y - 0.8)
  }

  ctx.y += SPACE.MD // Space after meta row
}

// ============================================================================
// RICH TEXT BLOCK — walks TipTap JSON, renders formatted text in jsPDF
// ============================================================================

const HEADING_FONT_SIZES: Record<number, number> = { 1: 16, 2: 14, 3: 12, 4: 11, 5: 10, 6: 10 }

type FontStyle = 'normal' | 'bold' | 'italic' | 'bolditalic'

interface TextRun {
  text: string
  style: FontStyle
}

const getInlineRuns = (node: any): TextRun[] => {
  if (!node.content) return []
  return (node.content as any[]).flatMap((child: any) => {
    if (child.text !== undefined) {
      const bold = child.marks?.some((m: any) => m.type === 'bold') ?? false
      const italic = child.marks?.some((m: any) => m.type === 'italic') ?? false
      const style: FontStyle = bold && italic ? 'bolditalic' : bold ? 'bold' : italic ? 'italic' : 'normal'
      return [{ text: child.text, style }]
    }
    return getInlineRuns(child)
  })
}

/** Render a paragraph of possibly mixed-format runs, with word wrapping */
const renderParagraphRuns = (ctx: LayoutContext, runs: TextRun[], indent = 0): void => {
  if (!runs.length) {
    ctx.y += FONT.DESCRIPTION_TEXT * 0.45
    return
  }

  const lineH = FONT.DESCRIPTION_TEXT * 0.45
  const maxX = PAGE.MARGIN + PAGE.CONTENT_WIDTH - indent
  let x = PAGE.MARGIN + indent

  ensureSpace(ctx, lineH * 2)

  for (const run of runs) {
    ctx.doc.setFontSize(FONT.DESCRIPTION_TEXT)
    ctx.doc.setFont('helvetica', run.style)
    ctx.doc.setTextColor(COLOR.PRIMARY)

    // Split into tokens preserving spaces for accurate width tracking
    const tokens = run.text.split(/(\s+)/)
    for (const token of tokens) {
      if (!token) continue
      const w = ctx.doc.getTextWidth(token)
      if (x + w > maxX && x > PAGE.MARGIN + indent) {
        ctx.y += lineH
        ensureSpace(ctx, lineH)
        x = PAGE.MARGIN + indent
        if (!token.trim()) continue // skip leading space on new line
      }
      if (token.trim()) ctx.doc.text(token, x, ctx.y)
      x += w
    }
  }
  ctx.y += lineH
}

const drawRichNode = (ctx: LayoutContext, node: any, indent = 0, listIndex?: number): void => {
  switch (node.type) {
    case 'heading': {
      const level = (node.attrs?.level as number) || 1
      const fontSize = HEADING_FONT_SIZES[level] ?? 11
      const lineH = fontSize * 0.45
      const text = getInlineRuns(node)
        .map((r) => r.text)
        .join('')
      const lines = ctx.doc.splitTextToSize(text, PAGE.CONTENT_WIDTH - indent)
      ensureSpace(ctx, lines.length * lineH + SPACE.SM)
      ctx.y += SPACE.XS
      ctx.doc.setFontSize(fontSize)
      ctx.doc.setFont('helvetica', 'bold')
      ctx.doc.setTextColor(COLOR.PRIMARY)
      for (const line of lines) {
        ctx.doc.text(line, PAGE.MARGIN + indent, ctx.y)
        ctx.y += lineH
      }
      ctx.y += SPACE.XS
      break
    }
    case 'paragraph': {
      const runs = getInlineRuns(node)
      renderParagraphRuns(ctx, runs, indent)
      ctx.y += 1
      break
    }
    case 'bulletList': {
      for (const item of node.content || []) drawRichNode(ctx, item, indent)
      break
    }
    case 'orderedList': {
      ;(node.content || []).forEach((item: any, i: number) => drawRichNode(ctx, item, indent, i + 1))
      break
    }
    case 'listItem': {
      const prefix = listIndex !== undefined ? `${listIndex}. ` : '• '
      ctx.doc.setFontSize(FONT.DESCRIPTION_TEXT)
      ctx.doc.setFont('helvetica', 'normal')
      ctx.doc.setTextColor(COLOR.PRIMARY)
      ensureSpace(ctx, FONT.DESCRIPTION_TEXT * 0.45)
      ctx.doc.text(prefix, PAGE.MARGIN + indent, ctx.y)
      const prefixW = ctx.doc.getTextWidth(prefix)
      for (const child of node.content || []) {
        if (child.type === 'paragraph') renderParagraphRuns(ctx, getInlineRuns(child), indent + prefixW)
        else drawRichNode(ctx, child, indent + prefixW + 4)
      }
      break
    }
    case 'blockquote': {
      const startY = ctx.y
      for (const child of node.content || []) drawRichNode(ctx, child, indent + 6)
      ctx.doc.setDrawColor(COLOR.MUTED)
      ctx.doc.setLineWidth(0.5)
      ctx.doc.line(PAGE.MARGIN + indent + 1, startY, PAGE.MARGIN + indent + 1, ctx.y)
      ctx.y += SPACE.XS
      break
    }
    default:
      for (const child of node.content || []) drawRichNode(ctx, child, indent)
  }
}

/** Renders a description block from EditorContent JSON with full formatting support */
const drawRichTextBlock = (ctx: LayoutContext, content: any, fallbackText?: string): void => {
  // Label
  ctx.doc.setFontSize(FONT.DESCRIPTION_LABEL)
  ctx.doc.setFont('helvetica', 'bold')
  ctx.doc.setTextColor(COLOR.LABEL)
  ctx.doc.text('DESCRIPTION', PAGE.MARGIN, ctx.y)
  ctx.y += SPACE.SM

  if (content?.content) {
    for (const node of content.content) drawRichNode(ctx, node)
  } else if (fallbackText) {
    ctx.doc.setFontSize(FONT.DESCRIPTION_TEXT)
    ctx.doc.setFont('helvetica', 'normal')
    ctx.doc.setTextColor(COLOR.PRIMARY)
    const lines = ctx.doc.splitTextToSize(fallbackText, PAGE.CONTENT_WIDTH)
    const lineH = FONT.DESCRIPTION_TEXT * 0.4
    for (const line of lines) {
      ensureSpace(ctx, lineH + 1)
      ctx.doc.text(line, PAGE.MARGIN, ctx.y)
      ctx.y += lineH
    }
  }

  ctx.y += SPACE.MD
}

// ============================================================================
// MEDIA — All images same size with badge
// ============================================================================

interface ImageData {
  dataUrl: string
  width: number
  height: number
}

const loadImage = async (url: string): Promise<ImageData | null> => {
  try {
    const response = await fetch(url)
    const blob = await response.blob()

    return new Promise((resolve) => {
      const img = new Image()

      img.onload = () => {
        const reader = new FileReader()

        reader.onloadend = () => {
          resolve({
            dataUrl: reader.result as string,
            width: img.naturalWidth,
            height: img.naturalHeight,
          })
        }
        reader.readAsDataURL(blob)
      }
      img.onerror = () => resolve(null)
      img.src = URL.createObjectURL(blob)
    })
  } catch {
    return null
  }
}

/** Collect all image URLs from the task data */
const collectImageUrls = (data: TaskPdfData): string[] => {
  const urls: string[] = []

  for (const subtask of data.sub_tasks) {
    if (subtask.content?.images) {
      for (const url of subtask.content.images) {
        if (url) urls.push(url)
      }
    }
  }
  return urls
}

/** Pre-load all images in parallel; returns a cache Map<url, ImageData | null> */
const preloadAllImages = async (urls: string[]): Promise<Map<string, ImageData | null>> => {
  const results = await Promise.allSettled(urls.map((url) => loadImage(url)))

  return new Map(
    urls.map((url, i) => [
      url,
      results[i].status === 'fulfilled' ? (results[i] as PromiseFulfilledResult<ImageData | null>).value : null,
    ])
  )
}

/**
 * Draw single image with badge "PHOTO X/N"
 * Uses object-contain: fixed container, image fits inside maintaining aspect ratio
 */
const drawImage = async (
  ctx: LayoutContext,
  imageUrl: string,
  index: number,
  total: number,
  cache: Map<string, ImageData | null>
): Promise<void> => {
  const imageData = cache.get(imageUrl) ?? null

  if (!imageData) return

  const containerWidth = MEDIA.CONTAINER_WIDTH
  const containerHeight = MEDIA.CONTAINER_HEIGHT

  // Object-contain: fit image in container while maintaining aspect ratio
  const imageRatio = imageData.width / imageData.height
  const containerRatio = containerWidth / containerHeight

  let width: number
  let height: number

  if (imageRatio > containerRatio) {
    // Image is wider than container — fit by width
    width = containerWidth
    height = width / imageRatio
  } else {
    // Image is taller than container — fit by height
    height = containerHeight
    width = height * imageRatio
  }

  // If doesn't fit — move to next page
  ensureSpace(ctx, containerHeight + MEDIA.GAP)

  // Center container horizontally on page
  const containerX = PAGE.MARGIN + (PAGE.CONTENT_WIDTH - containerWidth) / 2

  // Center image within container
  const x = containerX + (containerWidth - width) / 2
  const y = ctx.y + (containerHeight - height) / 2

  // Draw image
  ctx.doc.addImage(imageData.dataUrl, 'JPEG', x, y, width, height)

  // Badge: "PHOTO 1/2", "PHOTO 2/2", etc. — positioned at container top-left
  const badgeText = `PHOTO ${index + 1}/${total}`

  ctx.doc.setFontSize(FONT.BADGE)

  const badgeWidth = ctx.doc.getTextWidth(badgeText) + 6
  const badgeHeight = 4

  ctx.doc.setFillColor(COLOR.BADGE_BG)
  ctx.doc.roundedRect(containerX + 2, ctx.y + 2, badgeWidth, badgeHeight, 1, 1, 'F')

  ctx.doc.setFont('helvetica', 'bold')
  ctx.doc.setTextColor(COLOR.BADGE_TEXT)
  ctx.doc.text(badgeText, containerX + 5, ctx.y + 4.8)

  // Always advance by container height (same for all images)
  ctx.y += containerHeight + MEDIA.GAP
}

/**
 * Draw all images — same size, vertical stack
 */
const drawMediaBlock = async (
  ctx: LayoutContext,
  imageUrls: string[],
  cache: Map<string, ImageData | null>
): Promise<void> => {
  if (!imageUrls || imageUrls.length === 0) return

  const total = imageUrls.length

  for (let i = 0; i < imageUrls.length; i++) {
    await drawImage(ctx, imageUrls[i], i, total, cache)
  }
}

// ============================================================================
// TIME FORMATTING
// ============================================================================

const formatSchedule = (data: TaskPdfData): string => {
  const dateFormat = 'DD MMM YYYY'
  const timeFormat = 'HH:mm'

  const fromTs = dayjs(data.from_ts).format(keys.DEFAULT_STRING_FORMAT_TIMESTAMP)
  const deadline = dayjs(data.deadline).format(keys.DEFAULT_STRING_FORMAT_TIMESTAMP)

  const dayNames: Record<number, string> = {
    1: 'Mon',
    2: 'Tue',
    3: 'Wed',
    4: 'Thu',
    5: 'Fri',
    6: 'Sat',
    0: 'Sun',
  }

  if (data.is_recurring && data.recurrence_frequency) {
    const toTs = calculateTots({ is_recurring: data.is_recurring, deadline, formattedDeadline: deadline })

    if (data.recurrence_frequency === FrequencyType.DAY) {
      const days =
        data.days_of_week?.length === 7 ? 'Daily' : data.days_of_week?.map((d) => dayNames[d]).join(', ') || 'Daily'
      return `${dayjs(fromTs).format(dateFormat)} - ${dayjs(toTs).format(dateFormat)} | ${days} | ${dayjs(
        fromTs
      ).format(timeFormat)}-${dayjs(deadline).format(timeFormat)}`
    }
    if (data.recurrence_frequency === FrequencyType.WEEK) {
      return `${dayjs(fromTs).format(dateFormat)} - ${dayjs(toTs).format(dateFormat)} | Weekly`
    }
    if (data.recurrence_frequency === FrequencyType.MONTH) {
      return `${dayjs(fromTs).format(dateFormat)} - ${dayjs(toTs).format(dateFormat)} | Monthly`
    }
  }

  return `${dayjs(fromTs).format(dateFormat)} ${dayjs(fromTs).format(timeFormat)} - ${dayjs(deadline).format(
    dateFormat
  )} ${dayjs(deadline).format(timeFormat)}`
}

// ============================================================================
// RENDERERS
// ============================================================================

const renderTaskSummary = (ctx: LayoutContext, data: TaskPdfData): void => {
  drawTaskSummaryBlock(ctx, 'Task Title', data.title)

  const descContent = data.contents.find((c) => c.text?.trim() || c.rich_text)
  const description = descContent ? resolveText(descContent.text, descContent.rich_text) : ''

  if (description) {
    drawTaskSummaryBlock(ctx, 'Description', description)
  }

  drawTaskSummaryBlock(ctx, 'Schedule', formatSchedule(data))

  const priorityLabel = PRIORITY_LABELS[data.priority]
  const viewText = data.view !== null ? `View ${data.view}` : 'Not set'

  drawTwoColumnBlocks(ctx, { label: 'Priority', value: priorityLabel }, { label: 'View', value: viewText })

  const options: string[] = []

  if (data.is_individual_results) options.push('Individual responses required')
  if (data.send_schedule_push) options.push('Reminder notifications enabled')
  if (data.rework_limit !== null && data.rework_limit > 0) options.push(`Feedback limit: ${data.rework_limit}`)
  if (data.allow_completion_after_deadline) options.push('Completion after deadline allowed')
  // if (data.auto_approve_skipped_subtasks) options.push('Auto-approve skipped subtasks')

  drawOptionsBlock(ctx, options)
}

const renderSubtask = async (
  ctx: LayoutContext,
  subtask: TaskPdfSubtask,
  cache: Map<string, ImageData | null>
): Promise<void> => {
  const typeLabel = subtask.type === 2 ? 'PHOTO SUBTASK' : 'VIDEO SUBTASK'

  // Stripe
  drawSubtaskHeader(ctx, subtask.title, typeLabel)

  // Meta row (category + required)
  drawMetaRow(ctx, subtask.category, subtask.is_required)

  // Description — rich_text with full formatting, fallback to plain text
  if (subtask.content?.rich_text || subtask.content?.text) {
    drawRichTextBlock(ctx, subtask.content.rich_text, subtask.content.text ?? undefined)
  }

  // All images — same size, with badge
  if (subtask.content?.images && subtask.content.images.length > 0) {
    await drawMediaBlock(ctx, subtask.content.images, cache)
  }

  // Between subtasks
  ctx.y += SPACE.LG
}

const renderFooter = (doc: jsPDF): void => {
  const pageCount = doc.getNumberOfPages()

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(FONT.FOOTER)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(COLOR.MUTED)

    const pageH = doc.internal.pageSize.getHeight()
    doc.text(`${i}/${pageCount}`, PAGE.WIDTH - PAGE.MARGIN - 6, pageH - 6)

    if (i === pageCount) {
      doc.text(`Generated by Storee · ${dayjs().format('DD MMM YYYY')}`, PAGE.MARGIN, pageH - 6)
    }
  }
}

// ============================================================================
// MAIN EXPORT
// ============================================================================

export const generateTaskPdfBlob = async (data: TaskPdfData): Promise<Blob> => {
  // Pre-load all images ONCE in parallel so measurement and render use identical data.
  const allUrls = collectImageUrls(data)
  const imageCache = await preloadAllImages(allUrls)

  const subtasks = data.sub_tasks
    .filter((st) => st.type === 2 || st.type === 4)
    .sort((a, b) => a.order_index - b.order_index)

  // Measure task summary height (header + summary blocks only)
  const mSummaryDoc = new jsPDF({ unit: 'mm', format: [PAGE.WIDTH, 30000] })
  const mSummaryCtx: LayoutContext = { doc: mSummaryDoc, y: 0, singlePage: true }

  drawTopHeader(mSummaryCtx, 'Task Summary')
  renderTaskSummary(mSummaryCtx, data)

  const summaryH = mSummaryCtx.y + PAGE.MARGIN + 10

  // Measure each subtask height individually.
  // The PDF spec limits a single page to ~5080mm (14400pt). A task with many
  // images can easily exceed this as a single page, so we use one page per
  // subtask — each section stays well within the limit.
  const subtaskHeights: number[] = []

  for (const st of subtasks) {
    const mDoc = new jsPDF({ unit: 'mm', format: [PAGE.WIDTH, 30000] })
    const mCtx: LayoutContext = { doc: mDoc, y: PAGE.MARGIN, singlePage: true }
    await renderSubtask(mCtx, st, imageCache)
    subtaskHeights.push(mCtx.y + PAGE.MARGIN)
  }

  // Page 1: task summary
  const doc = new jsPDF({ unit: 'mm', format: [PAGE.WIDTH, summaryH] })
  const summaryCtx: LayoutContext = { doc, y: 0, singlePage: true }

  drawTopHeader(summaryCtx, 'Task Summary')
  renderTaskSummary(summaryCtx, data)

  // Pages 2..N: one page per subtask with custom height
  for (let i = 0; i < subtasks.length; i++) {
    doc.addPage([PAGE.WIDTH, subtaskHeights[i]])
    const stCtx: LayoutContext = { doc, y: PAGE.MARGIN, singlePage: true }
    await renderSubtask(stCtx, subtasks[i], imageCache)
  }

  renderFooter(doc)
  return doc.output('blob')
}

export const downloadTaskPdf = async (data: TaskPdfData, filename?: string): Promise<void> => {
  const blob = await generateTaskPdfBlob(data)
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')

  link.href = url
  link.download = filename || `${data.title.replace(/[^a-zA-Z0-9]/g, '_')}_task.pdf`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)
}
